import { zodResolver } from "@hookform/resolvers/zod";
import { t } from "@lingui/macro";
import { Flask, MagicWand, Sparkle } from "@phosphor-icons/react";
import { createResumeSchema, ResumeDto } from "@reactive-resume/dto";
import { idSchema, sampleResume } from "@reactive-resume/schema";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Tooltip,
} from "@reactive-resume/ui";
import { cn, generateRandomName, kebabCase } from "@reactive-resume/utils";
import { Mail, TriangleAlert, Video } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

import { useProductModules } from "@/client/services/product-modules";
import { useCreateResume, useDeleteResume, useUpdateResume } from "@/client/services/resume";
import { useImportResume } from "@/client/services/resume/import";
import { useAudioChatStore } from "@/client/stores/audioChatStore";
import { useDialog } from "@/client/stores/dialog";

import { AudioChatDialog } from "./audioChat";

const formSchema = createResumeSchema.extend({ id: idSchema.optional() });

type FormValues = z.infer<typeof formSchema>;

export const ResumeDialog = () => {
  const { isOpen, mode, payload, close } = useDialog<ResumeDto>("resume");
  const [isAudioChatOpen, setIsAudioChatOpen] = useState(false);
  const [spotlightBlock, setSpotlightBlock] = useState(false);
  const resetAudioChat = useAudioChatStore((state) => state.resetAudioChat);
  const navigate = useNavigate();
  // With Spotlight Intro hidden, the user can't remove their spotlight themselves: support can.
  const { isEnabled } = useProductModules();
  const canManageSpotlight = isEnabled("spotlight_intro");

  const isCreate = mode === "create";
  const isUpdate = mode === "update";
  const isDelete = mode === "delete";
  const isDuplicate = mode === "duplicate";

  const { createResume, loading: createLoading } = useCreateResume();
  const { updateResume, loading: updateLoading } = useUpdateResume();
  const { deleteResume, loading: deleteLoading } = useDeleteResume();
  const { importResume: duplicateResume, loading: duplicateLoading } = useImportResume();

  const loading = createLoading || updateLoading || deleteLoading || duplicateLoading;

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: "", slug: "" },
  });

  const handleCloseAudioChat = () => {
    setIsAudioChatOpen(false);
    resetAudioChat();
  };

  useEffect(() => {
    if (isOpen) {
      onReset();
      setSpotlightBlock(false);
    }
  }, [isOpen, payload]);

  useEffect(() => {
    const slug = kebabCase(form.watch("title"));
    form.setValue("slug", slug);
  }, [form.watch("title")]);

  const onSubmit = async (values: FormValues) => {
    if (isCreate) {
      await createResume({ slug: values.slug, title: values.title, visibility: "private" });
    }

    if (isUpdate) {
      if (!payload.item?.id) return;
      await updateResume({
        ...payload.item,
        title: values.title,
        slug: values.slug,
      });
    }

    if (isDuplicate) {
      if (!payload.item?.id) return;
      await duplicateResume({
        title: values.title,
        slug: values.slug,
        data: payload.item.data,
      });
    }

    if (isDelete) {
      if (!payload.item?.id) return;
      try {
        await deleteResume({ id: payload.item.id });
      } catch (err: any) {
        const status = err?.response?.status ?? err?.status;
        if (status === 409) {
          setSpotlightBlock(true);
          return;
        }
        throw err;
      }
    }

    close();
  };

  const onReset = () => {
    if (isCreate) form.reset({ title: "", slug: "" });
    if (isUpdate)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
    if (isDuplicate)
      form.reset({ title: `${payload.item?.title} (Copy)`, slug: `${payload.item?.slug}-copy` });
    if (isDelete)
      form.reset({ id: payload.item?.id, title: payload.item?.title, slug: payload.item?.slug });
  };

  const onGenerateRandomName = () => {
    const name = generateRandomName();
    form.setValue("title", name);
    form.setValue("slug", kebabCase(name));
  };

  const onCreateSample = async () => {
    const randomName = generateRandomName();
    const { title, slug } = form.getValues();

    await duplicateResume({
      title: title || randomName,
      slug: slug || kebabCase(randomName),
      data: sampleResume,
    });

    close();
  };

  const onCreateWithAudioChat = () => {
    console.log("onCreateWithAudioChat called");
    setIsAudioChatOpen(true);
    close();
  };

  const onCreateBlankResume = () => {
    // TODO: Implement creating a blank resume
    console.log("Create Blank Resume clicked");
    close();
  };

  if (isDelete) {
    if (spotlightBlock) {
      return (
        <AlertDialog open={isOpen} onOpenChange={close}>
          <AlertDialogContent className="max-w-md">
            <AlertDialogHeader>
              <div className="mb-1 flex items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400">
                  <TriangleAlert size={18} strokeWidth={2} />
                </span>
                <AlertDialogTitle className="text-base leading-snug">
                  {t`Resume cannot be deleted`}
                </AlertDialogTitle>
              </div>
              <AlertDialogDescription asChild>
                <div className="space-y-3 pt-1">
                  <p className="text-sm text-muted-foreground">
                    {t`This is your last resume and you have an active Spotlight Intro. Your Spotlight Intro is shown to recruiters alongside your resume.`}
                  </p>
                  <div className="flex items-start gap-2.5 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-3 dark:border-amber-800/40 dark:bg-amber-950/20">
                    <Video size={15} className="mt-0.5 shrink-0 text-amber-600 dark:text-amber-400" strokeWidth={2} />
                    <p className="text-xs leading-relaxed text-amber-800 dark:text-amber-300">
                      {canManageSpotlight
                        ? t`To delete this resume, go to Spotlight Intro and remove your active spotlight first.`
                        : t`To delete this resume, contact support@theresume.io and we'll remove your active spotlight first.`}
                    </p>
                  </div>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className="mt-2">
              <AlertDialogCancel>{t`Keep my resume`}</AlertDialogCancel>
              {canManageSpotlight ? (
                <AlertDialogAction
                  onClick={() => {
                    close();
                    navigate("/dashboard/spotlight-intro");
                  }}
                  className="gap-1.5 bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-500"
                >
                  <Video size={14} strokeWidth={2} />
                  {t`Manage Spotlight`}
                </AlertDialogAction>
              ) : (
                <AlertDialogAction
                  asChild
                  className="gap-1.5 bg-amber-500 text-white hover:bg-amber-600 focus-visible:ring-amber-500"
                >
                  {/* eslint-disable-next-line lingui/no-unlocalized-strings -- email address */}
                  <a href="mailto:support@theresume.io">
                    <Mail size={14} strokeWidth={2} />
                    {t`Email support`}
                  </a>
                </AlertDialogAction>
              )}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      );
    }

    return (
      <AlertDialog open={isOpen} onOpenChange={close}>
        <AlertDialogContent>
          <Form {...form}>
            <form>
              <AlertDialogHeader>
                <AlertDialogTitle>{t`Are you sure you want to delete your resume?`}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t`This action cannot be undone. This will permanently delete your resume and cannot be recovered.`}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <AlertDialogFooter>
                <AlertDialogCancel>{t`Cancel`}</AlertDialogCancel>
                <AlertDialogAction variant="error" onClick={form.handleSubmit(onSubmit)} disabled={loading}>
                  {loading ? t`Deleting...` : t`Delete`}
                </AlertDialogAction>
              </AlertDialogFooter>
            </form>
          </Form>
        </AlertDialogContent>
      </AlertDialog>
    );
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={close}>
        <DialogContent className="sm:max-w-[480px] bg-gradient-to-br from-blue-50/50 via-white to-purple-50/30 dark:from-blue-950/10 dark:via-background dark:to-purple-950/10 border-0 shadow-2xl">
          <DialogHeader className="text-center space-y-4 pb-2">
            <div className="relative mx-auto">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-400/20 to-purple-400/20 rounded-full blur-xl"></div>
              </div>
              <div className="relative inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl shadow-lg">
                <Sparkle size={32} color="#ffffff" />
              </div>
            </div>
            <DialogTitle className="text-2xl md:text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              {isCreate && t`Create Your Resume`}
              {isUpdate && t`Update Your Resume`}
              {isDuplicate && t`Duplicate Your Resume`}
            </DialogTitle>
            {isCreate && (
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                {t`Choose how you'd like to build your professional resume`}
              </p>
            )}
          </DialogHeader>
          <Form {...form}>
            <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
              {isCreate && (
                <div className="space-y-5">
                  <button
                    type="button"
                    onClick={onCreateWithAudioChat}
                    className="group relative w-full overflow-hidden rounded-2xl border-2 border-transparent bg-gradient-to-br from-purple-500 via-blue-500 to-cyan-500 p-0.5 shadow-xl transition-all duration-500 hover:scale-[1.02] hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-purple-500/20"
                  >
                    <div className="relative h-48 w-full overflow-hidden rounded-xl bg-white dark:bg-gray-900">
                      {/* Animated background waves */}
                      <div className="absolute -left-5 top-0 -mt-[70%] h-[400px] w-[400px] animate-wave rounded-[40%] bg-gradient-to-br from-purple-400/70 via-blue-400/70 to-cyan-400/70 opacity-80"></div>
                      <div className="absolute left-0 top-[120px] -mt-[70%] h-[400px] w-[400px] animate-wave-slow rounded-[40%] bg-gradient-to-br from-purple-500/60 via-blue-500/60 to-cyan-500/60 opacity-70"></div>
                      <div className="absolute left-0 top-[140px] -mt-[70%] h-[400px] w-[400px] animate-wave-slower rounded-[40%] bg-gradient-to-br from-purple-600/50 via-blue-600/50 to-cyan-600/50 opacity-60"></div>

                      {/* Content overlay */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center text-center text-white">
                        <div className="relative z-10 space-y-3">
                          <div className="inline-flex items-center justify-center w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl shadow-lg group-hover:scale-110 transition-transform duration-300">
                            <Sparkle size={28} color="#ffffff" />
                          </div>
                          <div className="space-y-1">
                            <h3 className="text-xl font-bold">{t`AI-Powered Resume`}</h3>
                            <p className="text-sm font-medium opacity-90">{t`Chat with our AI assistant`}</p>
                            <p className="text-xs opacity-75">{t`Get personalized guidance & suggestions`}</p>
                          </div>
                          <div className="inline-flex items-center gap-1 bg-white/20 backdrop-blur-sm rounded-full px-3 py-1 text-xs font-medium">
                            <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                            {t`Recommended`}
                          </div>
                        </div>
                      </div>
                    </div>
                  </button>
                  {/* <button
                    type="button"
                    onClick={onCreateBlankResume}
                    className="w-full rounded-lg border border-gray-200 p-4 text-left hover:bg-gray-50 hover:border hover:border-primary dark:hover:bg-secondary focus:outline-none shadow-md hover:scale-[1.01] transition-all duration-500"
                  >
                    <div className="flex items-center">
                      <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                        <File size={24} className="text-primary" />
                      </div>
                      <div className="ml-4">
                        <h3 className="text-lg text-primary dark:text-white font-semibold">{t`Create Blank Resume`}</h3>
                        <p className="text-sm text-primary dark:text-white">{t`Start with an empty template`}</p>
                      </div>
                    </div>
                  </button> */}
                  <button
                    type="button"
                    onClick={onCreateSample}
                    className="group relative w-full overflow-hidden rounded-xl border-2 border-gray-200/50 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 dark:border-gray-700/50 p-6 text-left shadow-lg transition-all duration-300 hover:scale-[1.02] hover:border-blue-300 hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-blue-500/20"
                  >
                    <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-400/10 to-purple-400/10 rounded-full -translate-y-10 translate-x-10 group-hover:scale-150 transition-transform duration-500"></div>
                    <div className="relative flex items-center space-x-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg group-hover:scale-110 transition-transform duration-300">
                        <Flask size={28} className="text-white" />
                      </div>
                      <div className="flex-1 space-y-1">
                        <h3 className="text-lg font-bold text-foreground group-hover:text-blue-600 transition-colors">{t`Sample Resume`}</h3>
                        <p className="text-sm text-muted-foreground">{t`Start with a pre-filled example template`}</p>
                        <div className="flex items-center gap-2 pt-1">
                          <div className="inline-flex items-center gap-1 bg-blue-50 dark:bg-blue-950/20 text-blue-700 dark:text-blue-300 rounded-full px-2 py-0.5 text-xs font-medium">
                            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                            {t`Quick Start`}
                          </div>
                        </div>
                      </div>
                      <div className="text-blue-500 group-hover:translate-x-1 transition-transform duration-300">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </div>
                  </button>
                </div>
              )}

              {!isCreate && (
                <div className="space-y-6 bg-white/60 dark:bg-card/60 backdrop-blur-sm rounded-xl border p-6">
                  <FormField
                    name="title"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel className="text-base font-semibold text-foreground">{t`Resume Title`}</FormLabel>
                        <FormControl>
                          <div className="flex items-center gap-3">
                            <Input 
                              {...field} 
                              className="flex-1 h-11 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-background focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200" 
                              placeholder={t`Enter resume title...`}
                            />
                            {(isCreate || isDuplicate) && (
                              <Tooltip content={t`Generate a random title for your resume`}>
                                <Button
                                  size="icon"
                                  type="button"
                                  variant="outline"
                                  onClick={onGenerateRandomName}
                                  className="h-11 w-11 rounded-lg border-2 border-gray-200 dark:border-gray-700 hover:border-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950/20 transition-all duration-200"
                                >
                                  <MagicWand size={18} />
                                </Button>
                              </Tooltip>
                            )}
                          </div>
                        </FormControl>
                        <FormDescription className="text-sm text-muted-foreground bg-blue-50 dark:bg-blue-950/20 rounded-lg p-3 border border-blue-200/50 dark:border-blue-800/30">
                          💡 {t`Tip: You can name the resume referring to the position you are applying for.`}
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    name="slug"
                    control={form.control}
                    render={({ field }) => (
                      <FormItem className="space-y-3">
                        <FormLabel className="text-base font-semibold text-foreground">{t`URL Slug`}</FormLabel>
                        <FormControl>
                          <Input 
                            {...field} 
                            className="h-11 rounded-lg border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-background focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all duration-200"
                            placeholder={t`url-friendly-name`}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="flex justify-end pt-2">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold px-8 py-2.5 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {loading ? (
                        <div className="flex items-center gap-2">
                          <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                          {isUpdate ? t`Saving...` : t`Duplicating...`}
                        </div>
                      ) : (
                        <>
                          {isUpdate && t`Save Changes`}
                          {isDuplicate && t`Duplicate Resume`}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </form>
          </Form>
        </DialogContent>
      </Dialog>
      <AudioChatDialog isOpen={isAudioChatOpen} onClose={() => handleCloseAudioChat()} />
    </>
  );
};
